import { HeightRangePair } from "../../system";
import { AbstractRectangleItem } from "./AbstractRectangleItem";
import { TypeKeys } from "./TypeKeys";
import { TextBoxItem } from "./TextBoxItem";
import { GraphItem } from "./GraphItem";
import { Rectangle, Geometry, Point, Size } from "../../drawing";
import { IItem } from "./IItem";
import { IAlternative } from "./IAlternative";
import { IDrawableItem } from "./IDrawableItem";
import { IClonable } from "./IClonable";
import { IQuotable } from ".";
import Enumerable from "linq";

export class RangeOfMotionItem extends AbstractRectangleItem implements IAlternative, IQuotable {
  constructor(
    graph: GraphItem,
    lowerMmtItem: TextBoxItem,
    higherMmtItem: TextBoxItem,
    lowerRomItem: TextBoxItem,
    higherRomItem: TextBoxItem
  ) {
    super(TypeKeys.RangeOfMotionItem);

    lowerMmtItem.adjustFontSize();
    higherMmtItem.adjustFontSize();
    lowerRomItem.adjustFontSize();
    higherRomItem.adjustFontSize();

    // グラフの余白を含んだサイズだと大きすぎるので余白は除いておく
    const graphRect = new Rectangle(graph.geometry.rectangle.topLeft.add(new Point(10, 11)), graph.geometry.rectangle.size.add(new Size(-20, -11)));
    const rectangles = [
      graphRect,
      lowerMmtItem.geometry.rectangle,
      higherMmtItem.geometry.rectangle,
      lowerRomItem.geometry.rectangle,
      higherRomItem.geometry.rectangle,
    ];
    this.privateId = graph.privateId;
    this.id = graph.id;
    this.geometry = new Geometry(this.calculateRectangle(rectangles));
    const offset = this.geometry.rectangle.topLeft.reverse();
    // V1で表示される位置に合わせこんでいる
    this._graphOffset = new Rectangle(offset.add(new Point(10, 11)), new Size(-20, -11));
    this.graph = this.createOffsetItem(graph, this._graphOffset);
    this._inputOffset = new Rectangle(offset);
    this.mmt = new HeightRangePair(
      this.createOffsetItem(lowerMmtItem, this._inputOffset),
      this.createOffsetItem(higherMmtItem, this._inputOffset));
    this.rom = new HeightRangePair(
      this.createOffsetItem(lowerRomItem, this._inputOffset),
      this.createOffsetItem(higherRomItem, this._inputOffset));
    this.name = this.graph.name;
    this.shouldQuote = true;
    this.supportedQuotable = true;
    this.isQuoted = false;
  }

  public graph: GraphItem;
  private _graphOffset: Rectangle;
  public mmt: HeightRangePair<TextBoxItem>;
  public rom: HeightRangePair<TextBoxItem>;
  private _inputOffset: Rectangle;

  public name: string;
  public supportedQuotable: boolean;
  public shouldQuote: boolean;
  public isQuoted: boolean;

  private calculateRectangle(rectangles: Rectangle[]): Rectangle {
    const left = rectangles.map(r => r.x).reduce((a, b) => Math.min(a, b));
    const right = rectangles.map(r => r.x + r.width).reduce((a, b) => Math.max(a, b));
    const top = rectangles.map(r => r.y).reduce((a, b) => Math.min(a, b));
    const bottom = rectangles.map(r => r.y + r.height).reduce((a, b) => Math.max(a, b));
    const width = right - left;
    const height = bottom - top;
    return new Rectangle(new Point(left, top), new Size(width, height));
  }

  private createOffsetItem<T extends IDrawableItem & IClonable<IItem>>(source: T, offset: Rectangle): T {
    const clone = source.clone() as T;
    clone.geometry = clone.geometry.add(offset);
    return clone;
  }

  get alternatedContents(): IItem[] {
    // H大に存在したcontentsが不足してる関節可動域オブジェクト救済用の実装.
    // alternatorが一時追加したコンテンツ(isAdditional=true)は代替コンテンツとして返さない.(返すとバックエンドに送られるので.)
    // 詳細はRangeOfMotionItemAlternatorのコメント参照.
    return Enumerable.from(this.alternatedAllContents)
      .where(c => !c.isAdditional)
      .toArray();
  }
  
  private get alternatedAllContents(): IItem[] {
    return [
      this.createOffsetItem(this.graph, this._graphOffset.reverse()),
      this.createOffsetItem(this.mmt.low, this._inputOffset.reverse()),
      this.createOffsetItem(this.mmt.high, this._inputOffset.reverse()),
      this.createOffsetItem(this.rom.low, this._inputOffset.reverse()),
      this.createOffsetItem(this.rom.high, this._inputOffset.reverse()),
    ];
  }
  
  protected createCloneInstance(): RangeOfMotionItem {
    const contents = this.alternatedAllContents;
    return new RangeOfMotionItem(
      contents[0] as GraphItem,
      contents[1] as TextBoxItem,
      contents[2] as TextBoxItem,
      contents[3] as TextBoxItem,
      contents[4] as TextBoxItem,
    );
  }

  protected applyMembers(clone: RangeOfMotionItem): void {
    super.applyMembers(clone);
    clone.graph = this.cloneValue(this.graph);
    clone._graphOffset = this.cloneValue(this._graphOffset);
    clone.rom = this.cloneValue(this.rom);
    clone.mmt = this.cloneValue(this.mmt);
    clone._inputOffset = this.cloneValue(this._inputOffset);
    clone.name = this.cloneValue(this.name);
    clone.supportedQuotable = this.cloneValue(this.supportedQuotable);
    clone.shouldQuote = this.cloneValue(this.shouldQuote);
    clone.isQuoted = this.cloneValue(this.isQuoted);
  }

  move(difference: Point): void {
    if (this.isPositionFixed) { return; }
    this.geometry.rectangle = this.geometry.rectangle.move(difference);
    const offset = this.geometry.rectangle.topLeft.reverse();
    this._graphOffset = new Rectangle(offset.add(new Point(10, 11)), new Size(-20, -11));
    this._inputOffset = new Rectangle(offset);
  }
}
